"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import React, { useEffect, useState } from "react";

/** Pause between two blinks when the university has not set one. */
export const DEFAULT_PROMO_BLINK_GAP_SECONDS = 2;

/**
 * How long a single pulse takes — fixed, so a blink always looks like a blink.
 * Matches the CSS fallback for `--promo-blink-duration` in globals.css.
 */
const BLINK_DURATION_SECONDS = 1.5;

/**
 * Intrinsic size handed to next/image. The RENDERED size is set by the classes below
 * (44px on mobile, 60px from `tab:` = 768px up) — a single inline width/height cannot be
 * media-queried, and a flat 60px icon is oversized on a phone.
 */
const ICON_SIZE = 60;
/** Icon box: 44px mobile → 60px desktop. Kept identical on the wrapper and the image. */
const ICON_BOX_CLASS = "size-11 tab:size-15";
/** The pulse ring starts 1/3 wider than the icon and scales to 1.5× from there. */
const PULSE_BOX_CLASS = "size-[59px] tab:size-20";

/**
 * Floating promotional icon shown once the promotional banner is dismissed.
 *
 * The pulsing halo is centred on the icon by anchoring it to the middle of a
 * fixed-size wrapper — `@keyframes pulse-border` supplies its own
 * `translate(-50%, -50%)`, so `top/left: 50%` is what actually centres it (any
 * Tailwind translate utility here would be overridden by the animation).
 *
 * Blink timing: one pulse always takes BLINK_DURATION_SECONDS, and the CMS value
 * is the quiet gap that follows it. The halo animation is therefore one-shot
 * (`forwards`, so it rests invisible) and is restarted by remounting the span on
 * a new `key` once every blink + gap. Driving the gap through the animation
 * duration instead — which is what this used to do — slowed the pulse itself
 * down to the setting, so "10 seconds" produced one ten-second-long expansion
 * rather than a normal blink every ten seconds.
 */
const OfferBox = ({
	className,
	onClick,
	animationColor = "yellow-600",
	promotionalIcon = "",
	blinkIntervalSeconds,
	blinkEnabled = true,
}: {
	className?: string;
	onClick?: () => void;
	animationColor?: string;
	promotionalIcon?: string;
	/** Quiet seconds BETWEEN two blinks; invalid/missing falls back to 2s. */
	blinkIntervalSeconds?: number | string | null;
	/**
	 * Page-level switch (`page.enable_promotional_icon_blink`). False → the icon renders
	 * exactly as it does now but perfectly still: no halo element and no interval timer.
	 * The button stays visible and clickable, so the banner can still be re-opened.
	 */
	blinkEnabled?: boolean;
}) => {
	const parsedGap = Number(blinkIntervalSeconds);
	const gapSeconds =
		Number.isFinite(parsedGap) && parsedGap > 0
			? parsedGap
			: DEFAULT_PROMO_BLINK_GAP_SECONDS;

	// Each tick remounts the halo, which restarts its one-shot pulse. The first
	// blink runs on mount, so the timer only needs to schedule the ones after it.
	const [blink, setBlink] = useState(0);
	useEffect(() => {
		// Blink switched off → never schedule the remount tick. The halo is not rendered
		// either, so there would be nothing to restart.
		if (!blinkEnabled) return;
		const cycleMs = (BLINK_DURATION_SECONDS + gapSeconds) * 1000;
		const timer = setInterval(() => setBlink((n) => n + 1), cycleMs);
		return () => clearInterval(timer);
	}, [gapSeconds, blinkEnabled]);

	return (
		<button
			id="play-video"
			className={cn(
				className,
				// Padding kept from the original so the icon's resting position in the
				// floating column is unchanged — only the halo moves.
				"cursor-pointer rounded-full border-0 bg-transparent pb-3 pl-1",
			)}
			onClick={onClick}
			aria-label="Open promotional offer"
		>
			{/* Fixed-size stage so the halo is centred on the icon at every breakpoint,
          and stays centred even before the icon image has loaded. */}
			<div
				className={cn(
					"relative flex items-center justify-center",
					ICON_BOX_CLASS,
				)}
			>
				{blinkEnabled && (
					<span
						key={blink}
						style={
							{
								backgroundColor: animationColor,
								"--promo-blink-duration": `${BLINK_DURATION_SECONDS}s`,
							} as React.CSSProperties
						}
						className={cn(
							"animate-pulse-custom pointer-events-none absolute top-1/2 left-1/2 rounded-full",
							PULSE_BOX_CLASS,
							// Behind the icon: the icon below is `relative z-10`.
							"z-0",
							{
								"bg-yellow-600": animationColor === "yellow-600",
							},
						)}
					/>
				)}
				{promotionalIcon && (
					<Image
						src={promotionalIcon}
						alt="Promotional Icon"
						width={ICON_SIZE}
						height={ICON_SIZE}
						className={cn("relative z-10 object-contain", ICON_BOX_CLASS)}
					/>
				)}
			</div>
		</button>
	);
};

export default OfferBox;
